import { useCallback, useState } from 'react';

import { api, type ClientContractor, type ClientDetail } from './api';
import { RevenueArea } from './components/Charts';
import {
  Badge,
  Card,
  CardHeader,
  Cell,
  ExportButton,
  Kpi,
  Row,
  SearchBar,
  Spinner,
  Table,
} from './components/Ui';
import { useAsyncData } from './hooks/useAsyncData';
import { useSortedRows } from './hooks/useSortedRows';
import { hours, marginPercent, money, moneyCompact, rate, shortDate } from './format';
import { Shell, type ShellProps } from './Shell';

export function Client360(props: ShellProps & { clientId: string }) {
  const { clientId, navigate } = props;
  const [search, setSearch] = useState('');

  const { data, error } = useAsyncData(
    () => api.client(clientId),
    [clientId],
    'Could not load customer.',
  );

  const filteredContractors =
    data !== null
      ? data.contractors.filter(
          (c) => search === '' || c.name.toLowerCase().includes(search.toLowerCase()),
        )
      : [];

  const accessor = useCallback((row: ClientContractor, key: string): unknown => {
    const map: Record<string, unknown> = {
      name: row.name,
      type: row.workerType,
      weeks: row.weeks,
      hours: row.hours,
      gpPerHour: row.grossProfitPerHour,
      billed: row.billed,
      grossProfit: row.grossProfit,
      margin: marginPercent(row.grossProfit, row.billed),
    };
    return map[key];
  }, []);

  const { sorted, sortKey, sortDirection, onSort } = useSortedRows(filteredContractors, accessor);

  if (error !== null) {
    return (
      <Shell {...props} title="Customer">
        <Card className="px-5 py-8">
          <p className="text-sm text-caution">{error}</p>
        </Card>
      </Shell>
    );
  }

  if (data === null) {
    return (
      <Shell {...props} title="Customer">
        <Card>
          <Spinner label="Loading customer detail…" />
        </Card>
      </Shell>
    );
  }

  const totalBilled = data.contractors.reduce((s, c) => s + Number(c.billed), 0).toFixed(4);
  const totalGp = data.contractors.reduce((s, c) => s + Number(c.grossProfit), 0).toFixed(4);
  const totalHours = data.contractors.reduce((s, c) => s + Number(c.hours), 0).toFixed(4);

  return (
    <Shell {...props} title={data.name}>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('')}
          className="text-xs text-ink-faint hover:text-ink transition-colors"
        >
          ← Back to operations
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-baseline gap-4">
        <h1 className="text-xl font-semibold text-ink">{data.name}</h1>
        {data.paymentTerms !== null && (
          <span className="rounded-md bg-surface-overlay px-2 py-0.5 text-xs font-medium text-ink-muted">
            {data.paymentTerms}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Kpi
          label="Billed"
          value={moneyCompact(totalBilled)}
          exact={money(totalBilled)}
          tone="revenue"
          delay={0}
        />
        <Kpi
          label="Gross profit"
          value={moneyCompact(totalGp)}
          exact={money(totalGp)}
          tone="profit"
          delay={40}
        />
        <Kpi label="Hours" value={hours(totalHours)} delay={80} />
        <Kpi label="Contractors" value={String(data.contractors.length)} delay={120} />
        <Kpi
          label="GP margin"
          value={marginPercent(totalGp, totalBilled)}
          tone="profit"
          delay={160}
        />
      </div>

      {data.trend.length > 1 && (
        <Card className="mt-4" delay={200}>
          <CardHeader
            title="Revenue trend"
            subtitle="Billed revenue and gross profit by week for this customer"
          />
          <RevenueArea
            points={data.trend.map((t) => ({
              label: t.weekEnding,
              billed: t.billed,
              grossProfit: t.grossProfit,
            }))}
          />
        </Card>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2" delay={240}>
          <CardHeader
            title="Contractors on assignment"
            subtitle="Click a contractor to see their full history"
            aside={
              <div className="flex items-center gap-3">
                <div className="w-48">
                  <SearchBar
                    value={search}
                    onChange={setSearch}
                    placeholder="Filter contractors…"
                  />
                </div>
                <ExportButton
                  data={sorted.map((c) => ({
                    name: c.name,
                    type: c.workerType === 'contractor_1099' ? '1099' : 'W2',
                    weeks: c.weeks,
                    hours: hours(c.hours),
                    gpPerHour: rate(c.grossProfitPerHour),
                    billed: money(c.billed),
                    grossProfit: money(c.grossProfit),
                    margin: marginPercent(c.grossProfit, c.billed),
                  }))}
                  filename={`client-${data.name.toLowerCase().replace(/\s+/g, '-')}`}
                  headers={[
                    { key: 'name', label: 'Contractor' },
                    { key: 'type', label: 'Type' },
                    { key: 'weeks', label: 'Weeks' },
                    { key: 'hours', label: 'Hours' },
                    { key: 'gpPerHour', label: 'GP/h' },
                    { key: 'billed', label: 'Billed' },
                    { key: 'grossProfit', label: 'Gross Profit' },
                    { key: 'margin', label: 'Margin' },
                  ]}
                />
              </div>
            }
          />
          <Table
            head={[
              { label: 'Contractor', sortKey: 'name' },
              { label: 'Type', sortKey: 'type' },
              { label: 'Weeks', align: 'right', sortKey: 'weeks' },
              { label: 'Hours', align: 'right', sortKey: 'hours' },
              { label: 'GP/h', align: 'right', sortKey: 'gpPerHour' },
              { label: 'Billed', align: 'right', sortKey: 'billed' },
              { label: 'Gross profit', align: 'right', sortKey: 'grossProfit' },
              { label: 'Margin', align: 'right', sortKey: 'margin' },
            ]}
            sortKey={sortKey}
            sortDirection={sortDirection}
            onSort={onSort}
          >
            {sorted.map((c) => (
              <Row key={c.id} onClick={() => navigate(`contractor/${c.id}`)}>
                <Cell>
                  <span className="cursor-pointer text-ink transition-colors hover:text-profit">
                    {c.name}
                  </span>
                </Cell>
                <Cell muted>{c.workerType === 'contractor_1099' ? '1099' : 'W2'}</Cell>
                <Cell align="right" mono>
                  {c.weeks}
                </Cell>
                <Cell align="right" mono>
                  {hours(c.hours)}
                </Cell>
                <Cell align="right" mono>
                  {rate(c.grossProfitPerHour)}
                </Cell>
                <Cell align="right" mono>
                  {money(c.billed)}
                </Cell>
                <Cell align="right" mono>
                  <span className="text-profit">{money(c.grossProfit)}</span>
                </Cell>
                <Cell align="right" mono muted>
                  {marginPercent(c.grossProfit, c.billed)}
                </Cell>
              </Row>
            ))}
          </Table>
        </Card>

        <Card delay={280}>
          <CardHeader title="Accounts receivable" />
          <ArAgingCard
            invoices={data.invoices}
            arSummary={data.arSummary}
            paymentTerms={data.paymentTerms}
          />
        </Card>
      </div>
    </Shell>
  );
}

function ArAgingCard({
  invoices,
  arSummary,
  paymentTerms,
}: {
  invoices: ClientDetail['invoices'];
  arSummary: ClientDetail['arSummary'];
  paymentTerms: string | null;
}) {
  const total = Number(arSummary.totalOutstanding);
  const buckets = [
    { label: 'Current', amount: arSummary.current, color: 'bg-emerald-500' },
    { label: '31–60 days', amount: arSummary.thirtyDays, color: 'bg-amber-400' },
    { label: '61–90 days', amount: arSummary.sixtyDays, color: 'bg-orange-500' },
    { label: '90+ days', amount: arSummary.ninetyPlus, color: 'bg-red-500' },
  ];

  const today = new Date();

  function deriveStatus(inv: ClientDetail['invoices'][number]): string {
    if (inv.status === 'paid') return 'paid';
    if (inv.status === 'part_paid') return 'part_paid';
    if (
      inv.dueDate !== null &&
      new Date(inv.dueDate + 'T12:00:00Z') < today &&
      inv.status !== 'paid'
    ) {
      return 'overdue';
    }
    return inv.status;
  }

  const statusTone = (status: string): 'real' | 'warning' | 'neutral' => {
    if (status === 'paid') return 'real';
    if (status === 'part_paid' || status === 'overdue') return 'warning';
    return 'neutral';
  };

  return (
    <div className="px-5 py-4 space-y-4">
      {paymentTerms !== null && (
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint">
            Payment terms
          </p>
          <p className="mt-1 text-sm font-medium text-ink">{paymentTerms}</p>
        </div>
      )}

      <div>
        <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint">
          Total outstanding
        </p>
        <p className="figure mt-1 text-lg font-semibold text-caution">
          {money(arSummary.totalOutstanding)}
        </p>
      </div>

      {total > 0 && (
        <div className="space-y-2">
          <div className="flex h-3 w-full overflow-hidden rounded-full">
            {buckets.map((b) => {
              const pct = (Number(b.amount) / total) * 100;
              if (pct <= 0) return null;
              return (
                <div
                  key={b.label}
                  className={`${b.color} transition-all`}
                  style={{ width: `${String(pct)}%` }}
                  title={`${b.label}: ${money(b.amount)}`}
                />
              );
            })}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {buckets.map((b) => (
              <div key={b.label} className="flex items-center gap-2">
                <span className={`h-2.5 w-2.5 rounded-full ${b.color}`} />
                <span className="text-xs text-ink-muted">{b.label}</span>
                <span className="figure ml-auto text-xs font-medium text-ink">
                  {money(b.amount)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {invoices.length > 0 && (
        <div className="mt-2 -mx-5">
          <Table
            head={[
              { label: 'Invoice' },
              { label: 'Issue Date' },
              { label: 'Due Date' },
              { label: 'Amount', align: 'right' },
              { label: 'Paid', align: 'right' },
              { label: 'Status' },
            ]}
          >
            {invoices.map((inv) => {
              const status = deriveStatus(inv);
              return (
                <Row key={inv.id}>
                  <Cell>{inv.invoiceNumber}</Cell>
                  <Cell muted>{shortDate(inv.issueDate)}</Cell>
                  <Cell muted>{inv.dueDate !== null ? shortDate(inv.dueDate) : '—'}</Cell>
                  <Cell align="right" mono>
                    {money(inv.amount)}
                  </Cell>
                  <Cell align="right" mono>
                    {money(inv.paidAmount)}
                  </Cell>
                  <Cell>
                    <Badge tone={statusTone(status)}>{status.replace('_', ' ')}</Badge>
                  </Cell>
                </Row>
              );
            })}
          </Table>
        </div>
      )}
    </div>
  );
}
